import { FetcherStore } from "@nanostores/query";
import { useStore } from "@nanostores/react";
import { ComponentType, ReactElement, useEffect, useRef } from "react";

import { ErrorMessage } from "../components/ErrorMessage.tsx";
import { LoadingIndicator } from "../components/LoadingIndicator.tsx";

type WithDataLoadedOptions<TProp extends string, TData> = {
  loadingFallback?: ReactElement;
  propKey: TProp;
  refreshInterval?: number; // Polling interval in milliseconds
  shouldStopPolling?: (data: TData) => boolean; // Function to determine if polling should stop
};

export const withDataLoaded = <
  TData,
  TProp extends string,
  P extends { [K in TProp]?: TData },
>(
  store: FetcherStore<TData | undefined>,
  Component: ComponentType<P>,
  options: WithDataLoadedOptions<TProp, TData>,
): ComponentType<Omit<P, TProp>> => {
  const { loadingFallback, propKey, refreshInterval, shouldStopPolling } =
    options;

  const Wrapped = (props: Omit<P, TProp>): ReactElement => {
    const storeValue = useStore(store);
    const { data, error, loading } = storeValue;
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const hasLoadedDataRef = useRef<boolean>(false);

    // Track when data has been loaded at least once to distinguish initial load from polling updates
    useEffect(() => {
      if (data) {
        hasLoadedDataRef.current = true;
      }
    }, [data]);

    // Stop polling on error or when stop condition is met
    useEffect(() => {
      if (!intervalRef.current) {
        return;
      }

      // Stop on error if data has been loaded
      if (error && hasLoadedDataRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        return;
      }

      // Stop if stop condition is met
      if (data && shouldStopPolling && shouldStopPolling(data)) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, [data, error, shouldStopPolling]);

    // Set up polling if refreshInterval is provided
    useEffect(() => {
      if (refreshInterval && refreshInterval > 0) {
        void store.fetch();

        intervalRef.current = setInterval(() => {
          void store.fetch();
        }, refreshInterval);

        return (): void => {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        };
      }
    }, [refreshInterval, store]);

    const isInitialLoad = loading && !hasLoadedDataRef.current;

    if (isInitialLoad) {
      return loadingFallback ?? <LoadingIndicator />;
    } else if (error && !hasLoadedDataRef.current) {
      return <ErrorMessage message={error.message} />;
    } else if (data) {
      const injectedProp = { [propKey]: data } as Record<TProp, TData>;
      const fullProps = { ...props, ...injectedProp } as P;

      return <Component {...fullProps} />;
    }
    // should never get here
    return <ErrorMessage message="no data and no error" />;
  };

  return Wrapped;
};
